/**
 * Inventory Service - API integration for inventory management
 * 
 * Features:
 * - Get inventory logs/history
 * - Restock products
 * - Adjust stock (damage, return, correction)
 */

import api from './api';

// Inventory reasons matching backend enum
export const InventoryReason = {
  RESTOCK: 'restock',
  ORDER: 'order',
  ADJUSTMENT: 'adjustment',
  CANCELLATION: 'cancellation'
};

const inventoryService = {
  /**
   * Get inventory logs/history
   * @param {number} productId - Optional filter by product ID
   * @returns {Promise<Array>} List of inventory logs
   */
  getLogs: async (productId = null) => {
    const params = productId ? { productId } : {};
    const response = await api.get('/inventory/logs', { params });
    return response.data;
  },

  /**
   * Restock a product (add positive quantity)
   * @param {number} productId - Product ID to restock
   * @param {number} quantity - Quantity to add (must be positive)
   * @param {string} note - Optional note for the log
   * @returns {Promise<Object>} Created inventory log
   */
  restock: async (productId, quantity, note = '') => {
    const response = await api.post('/inventory/restock', {
      productId,
      quantity,
      note: note || undefined
    });
    return response.data;
  },

  /**
   * Adjust stock (can be positive or negative)
   * @param {number} productId - Product ID to adjust
   * @param {number} quantityChange - Quantity change (+/-)
   * @param {string} reason - Reason from InventoryReason enum
   * @param {string} note - Optional note for the log
   * @returns {Promise<Object>} Created inventory log
   */
  adjust: async (productId, quantityChange, reason, note = '') => {
    const response = await api.post('/inventory/adjust', {
      productId,
      quantityChange,
      reason,
      note: note || undefined
    });
    return response.data;
  },

  /**
   * Get low stock products (helper - filters from product list)
   * @param {Array} products - List of products
   * @param {number} threshold - Low stock threshold (default: 10)
   * @returns {Array} Products with stock below threshold
   */
  getLowStockProducts: (products, threshold = 10) => {
    return products.filter(p => 
      p.isAvailable &&
      p.stockQuantity !== undefined && 
      p.stockQuantity <= threshold && 
      p.stockQuantity > 0
    );
  },

  /**
   * Get out of stock products
   * @param {Array} products - List of products
   * @returns {Array} Products with zero stock
   */
  getOutOfStockProducts: (products) => {
    return products.filter(p => 
      p.isAvailable &&
      (!p.inStock || p.stockQuantity === 0)
    );
  }
};

export default inventoryService;